import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HttpService } from '../services/app/http.service';
import { DataService } from '../services/app/data.service';
import { SiteService } from '../services/app/site.service';
import { EmbedContextService } from '../services/app/embed-context.service';
import { catchError, concatMap, defer, from, of } from 'rxjs';
import { firstValueFrom } from 'rxjs';

/**
 * Entrée embed : `/embed?token=…&redirectafter=…` avec option `site_id` (entier > 0).
 * Après `/user/login-back`, si `site_id` est présent et fait partie de `user.sites`,
 * il est enregistré comme site sélectionné (stockage `site_selectionne`) pour aligner
 * l’accueil et les appels API sur le site du parent.
 */
function parseOptionalEmbedSiteId(raw: unknown): number | null {
  if (typeof raw !== 'string' || raw === '') {
    return null;
  }
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export const embedAuthGuard: CanActivateFn = (route, state) => {
  const embed = inject(EmbedContextService);
  const http = inject(HttpService);
  const router = inject(Router);
  const dataService = inject(DataService);
  const siteService = inject(SiteService);
  const { redirectafter, token } = route.queryParams;

  return defer(() => embed.setEmbedContextTrue()).pipe(
    concatMap(_ => {
      if (typeof redirectafter !== 'string' || typeof token !== 'string') {
        return of(router.parseUrl('/login'));
      }
      return http.apiLoginBack(token).pipe(
        concatMap(() =>
          from((async () => {
            const siteId = parseOptionalEmbedSiteId(route.queryParams['site_id']);
            if (siteId !== null) {
              const user = await firstValueFrom(dataService.user.get());
              const allowed = user?.sites?.some(s => s.id === siteId) ?? false;
              if (allowed) {
                await siteService.setSite(siteId);
              }
            }
            return router.parseUrl(redirectafter);
          })())
        ),
        catchError(_ => of(router.parseUrl('/login'))),
      );
    })
  );
};
